import moment from "moment";
import type { TPost } from "@/utils/validators";
import { BookmarkIcon } from "./ui/bookmark-icon";
import { UserAvatar } from "./ui/user-avatar";
import { useBookmarks } from "@/hooks/useBookmarks";
import { CommentsIcon } from "./ui/comments-icon";
import { ReactIcon } from "./ui/react-icon";
import { CopyUrlIcon } from "./ui/copy-url-icon";
import { toast } from "sonner";

type PropsWithPost = {
  post: TPost;
};

export function CardDetails({ post }: PropsWithPost) {
  const { bookmarkedPosts, addBookmark, removeBookmark } =
    useBookmarks("CardDetails");

  const isBookmarked = bookmarkedPosts.some((p) => p.id === post.id);

  function toggleBookmark() {
    if (isBookmarked) {
      removeBookmark(post.id);
      toast.warning("Bookmark removed");
    } else {
      addBookmark(post);
      toast.success("Post bookmarked!");
    }
  }

  function copyUrl() {
    navigator.clipboard.writeText(post.url);

    toast.success("Post URL copied to clipboard!");
  }

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#1a2029] transition-all duration-200 ease-out hover:border-m-border-strong hover:bg-m-surface-2 hover:shadow-lg">
      <div className="flex flex-col flex-1 p-5 pb-0">
        <div className="flex items-center mb-4 -space-x-2 rtl:space-x-reverse">
          <UserAvatar post={post} width={40} height={40} includeName={false} />
        </div>
        <a href={post.url} target="_blank" rel="noopener noreferrer">
          <h2 className="mb-3 text-lg font-extrabold leading-[1.35] tracking-[-0.03em] line-clamp-3 text-white">
            {post.title}
          </h2>
        </a>
        <div className="mb-2.5 flex flex-wrap gap-1.5 relative z-[2]">
          {post.tag_list.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-[#242a34] px-3 py-1 text-[13px] font-semibold text-[#75809a] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]"
            >
              #{tag}
            </span>
          ))}
        </div>
        <p className="mb-4 flex items-center gap-1.5 text-sm font-medium text-[#5f6a81]">
          {moment(post.created_at).fromNow()} - {post.reading_time_minutes} min
          read
        </p>
      </div>
      <div className="px-5">
        <UserAvatar post={post} width={20} height={20} />
      </div>
      <a href={post.url} target="_blank" rel="noopener noreferrer">
        <div className="overflow-hidden relative">
          <img
            src={post.social_image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover"
          />
        </div>
      </a>
      <div className="flex items-center justify-around relative z-[2] border-t border-white/5 bg-[#10151d] px-6 py-4 text-[#5f6a81]">
        <button
          className="flex gap-2 place-items-center cursor-pointer"
          onClick={toggleBookmark}
          title={isBookmarked ? "Remove Bookmark" : "Add Bookmark"}
        >
          <BookmarkIcon isBookmarked={isBookmarked} />
        </button>
        <button
          className="flex gap-2 place-items-center cursor-pointer"
          title="Comments"
        >
          <CommentsIcon /> {post.comments_count}
        </button>
        <button
          className="flex gap-2 place-items-center cursor-pointer"
          title="Reactions"
        >
          <ReactIcon /> {post.public_reactions_count}
        </button>
        <button
          className="flex gap-2 place-items-center cursor-pointer"
          title="Copy URL"
          onClick={copyUrl}
        >
          <CopyUrlIcon />
        </button>
      </div>
    </article>
  );
}
